import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Calendar, Clock, User, MessageSquare, CheckCircle, XCircle, Plus,
  Search, Scissors, Smartphone, Inbox, X, BellRing, PlayCircle,
  CalendarDays, Users, CircleCheckBig, Sparkles, ChevronRight, MapPin
} from "lucide-react";
import styles from "../../styles/Booking.module.css";
import toast, { Toaster } from "react-hot-toast";

axios.defaults.withCredentials = true;

const fallbackServices = ["Standard Haircut", "Pedicure", "Basic Facial", "Hair Tinting", "Braiding & Styling", "Wig Installation", "Lash Extensions", "Dreadlocks"];

export default function Booking() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeView, setActiveView] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState(fallbackServices);
  const [formData, setFormData] = useState({ client_name: "", phone: "", service: fallbackServices[0], date: "", time: "", send_reminder: true, source: "admin" });

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`/api/get-bookings`);
      setBookings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBookings(); axios.get("/api/services/public").then(r=>{ const names=(r.data||[]).map(x=>x.name); if(names.length){ setServices(names); setFormData(f=>({...f,service:names.includes(f.service)?f.service:names[0]})); }}).catch(()=>{}); }, [fetchBookings]);

  const handleSaveBooking = async () => {
    if (!formData.client_name || !formData.phone || !formData.date || !formData.time) return toast.error("Please fill all required fields.");
    try {
      await axios.post('/api/bookings/create', formData);
      toast.success("Appointment created successfully.");
      setShowAddModal(false);
      setFormData({ client_name: "", phone: "", service: services[0] || fallbackServices[0], date: "", time: "", send_reminder: true, source: "admin" });
      fetchBookings();
    } catch (err) { toast.error("Unable to create appointment."); }
  };

  const handleManualReminder = async (booking) => {
    try { await axios.post(`/api/bookings/send-reminder`, { id: booking.id }); toast.success(`Reminder sent to ${booking.client_name}`); }
    catch { toast.error("Failed to send reminder."); }
  };
  const handleCheckIn = async (booking) => {
    try { await axios.post(`/api/bookings/check-in`, { id: booking.id }); toast.success(`${booking.client_name} checked in.`); fetchBookings(); }
    catch { toast.error("Check-in failed."); }
  };
  const handleCompleteBooking = async (booking) => {
    if (!window.confirm("Mark this appointment as completed?")) return;
    try { await axios.post(`/api/bookings/complete`, { id: booking.id }); toast.success("Appointment completed."); fetchBookings(); }
    catch { toast.error("Action failed."); }
  };

  const todayKey = new Date().toISOString().slice(0,10);
  const statusOf = b => String(b.status || "pending").toLowerCase();
  const dateOf = b => String(b.booking_date || b.date || "").slice(0,10);
  const filteredBookings = useMemo(() => bookings.filter(b => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = `${b.client_name || ""} ${b.phone || ""} ${b.service || ""}`.toLowerCase().includes(q);
    const status = statusOf(b);
    const matchesView = activeView === "all" || (activeView === "today" && dateOf(b) === todayKey) || status === activeView;
    return matchesSearch && matchesView;
  }), [bookings, searchTerm, activeView, todayKey]);

  const today = bookings.filter(b => dateOf(b) === todayKey);
  const inService = bookings.filter(b => statusOf(b) === "in-progress").length;
  const completed = bookings.filter(b => statusOf(b) === "completed").length;
  const pending = bookings.filter(b => ["pending","confirmed"].includes(statusOf(b))).length;

  const niceDate = value => {
    if (!value) return "Date not set";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("en-NG", { day:"numeric", month:"short", year:"numeric" });
  };

  if (loading) return <div className={styles.loading}>Loading appointments…</div>;

  return (
    <div className={styles.page}>
      <Toaster position="top-right" />
      <section className={styles.hero}>
        <div>
          <div className={styles.eyebrow}><CalendarDays size={15}/> APPOINTMENT MANAGEMENT</div>
          <h1>Bookings</h1>
          <p>Manage appointments, walk-ins, reminders and client check-ins from one place.</p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setShowAddModal(true)}><Plus size={18}/> New Booking</button>
      </section>

      <section className={styles.stats}>
        <Stat icon={<Calendar size={20}/>} label="Today" value={today.length} hint="appointments scheduled" />
        <Stat icon={<Clock size={20}/>} label="Waiting" value={pending} hint="pending or confirmed" />
        <Stat icon={<Sparkles size={20}/>} label="In Service" value={inService} hint="clients being attended" />
        <Stat icon={<CircleCheckBig size={20}/>} label="Completed" value={completed} hint="finished appointments" />
      </section>

      <section className={styles.workspace}>
        <div className={styles.toolbar}>
          <div className={styles.tabs}>
            {[['all','All'],['today','Today'],['pending','Pending'],['confirmed','Confirmed'],['in-progress','In Service'],['completed','Completed']].map(([key,label]) => (
              <button key={key} className={activeView === key ? styles.activeTab : ""} onClick={() => setActiveView(key)}>{label}</button>
            ))}
          </div>
          <div className={styles.search}><Search size={17}/><input value={searchTerm} placeholder="Search client, phone or service" onChange={e=>setSearchTerm(e.target.value)}/></div>
        </div>

        <div className={styles.sectionHead}>
          <div><h2>Appointment List</h2><p>{filteredBookings.length} appointment{filteredBookings.length === 1 ? '' : 's'} shown</p></div>
          <span className={styles.location}><MapPin size={14}/> Dey Fresh Signature</span>
        </div>

        {filteredBookings.length ? (
          <>
            <div className={styles.desktopTable}>
              <table><thead><tr><th>Client</th><th>Service</th><th>Date & Time</th><th>Source</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>{filteredBookings.map(b => <BookingRow key={b.id} booking={b} niceDate={niceDate} onReminder={handleManualReminder} onCheckIn={handleCheckIn} onComplete={handleCompleteBooking}/>)}</tbody></table>
            </div>
            <div className={styles.mobileCards}>{filteredBookings.map(b => <BookingCard key={b.id} booking={b} niceDate={niceDate} onReminder={handleManualReminder} onCheckIn={handleCheckIn} onComplete={handleCompleteBooking}/>)}</div>
          </>
        ) : <div className={styles.empty}><div className={styles.emptyIcon}><Inbox size={30}/></div><h3>No appointments found</h3><p>Try another filter or create a new booking.</p><button onClick={()=>setShowAddModal(true)}><Plus size={16}/> New Booking</button></div>}
      </section>

      <button className={styles.mobileFab} onClick={()=>setShowAddModal(true)}><Plus size={20}/> New Booking</button>

      {showAddModal && <div className={styles.modalOverlay} onMouseDown={e => e.target === e.currentTarget && setShowAddModal(false)}>
        <div className={styles.modal}>
          <div className={styles.modalHead}><div><span>NEW APPOINTMENT</span><h3>Add a booking</h3><p>Enter the client's appointment details below.</p></div><button onClick={()=>setShowAddModal(false)}><X size={20}/></button></div>
          <div className={styles.modalBody}>
            <div className={styles.formGrid}>
              <Field icon={<User/>} label="Client name"><input value={formData.client_name} placeholder="Full name" onChange={e=>setFormData({...formData,client_name:e.target.value})}/></Field>
              <Field icon={<Smartphone/>} label="Phone number"><input value={formData.phone} placeholder="080..." onChange={e=>setFormData({...formData,phone:e.target.value})}/></Field>
              <Field icon={<Scissors/>} label="Service"><select value={formData.service} onChange={e=>setFormData({...formData,service:e.target.value})}>{services.map(s=><option key={s}>{s}</option>)}</select></Field>
              <Field icon={<Calendar/>} label="Appointment date"><input type="date" value={formData.date} onChange={e=>setFormData({...formData,date:e.target.value})}/></Field>
              <Field icon={<Clock/>} label="Appointment time"><input type="time" value={formData.time} onChange={e=>setFormData({...formData,time:e.target.value})}/></Field>
              <div className={styles.reminder}><MessageSquare size={19}/><div><strong>WhatsApp confirmation</strong><span>Send appointment confirmation to the client</span></div><input type="checkbox" checked={formData.send_reminder} onChange={e=>setFormData({...formData,send_reminder:e.target.checked})}/></div>
            </div>
          </div>
          <div className={styles.modalFooter}><button className={styles.secondaryBtn} onClick={()=>setShowAddModal(false)}>Cancel</button><button className={styles.primaryBtn} onClick={handleSaveBooking}>Create Booking <ChevronRight size={17}/></button></div>
        </div>
      </div>}
    </div>
  );
}

function Stat({icon,label,value,hint}) { return <div className={styles.stat}><div className={styles.statIcon}>{icon}</div><div><span>{label}</span><strong>{value}</strong><small>{hint}</small></div></div>; }
function Field({icon,label,children}) { return <label className={styles.field}><span>{React.cloneElement(icon,{size:14})}{label}</span>{children}</label>; }
function Status({value}) { const s=String(value||'pending').toLowerCase(); return <span className={`${styles.status} ${styles[`status_${s.replace('-','_')}`] || ''}`}>{s.replace('-',' ')}</span>; }
function Actions({booking,onReminder,onCheckIn,onComplete}) { const s=String(booking.status||'pending').toLowerCase(); return <div className={styles.actions}>{['pending','confirmed'].includes(s)&&<><button title="Send reminder" onClick={()=>onReminder(booking)}><BellRing size={16}/></button><button title="Check in" onClick={()=>onCheckIn(booking)}><PlayCircle size={16}/></button></>}{s==='in-progress'&&<button title="Complete" onClick={()=>onComplete(booking)}><CheckCircle size={16}/></button>}<a title="WhatsApp client" href={`https://wa.me/${String(booking.phone||'').replace(/\D/g,'')}`} target="_blank" rel="noreferrer"><MessageSquare size={16}/></a></div>; }
function BookingRow({booking,niceDate,...actions}) { return <tr><td><div className={styles.client}><div>{(booking.client_name||'?').charAt(0).toUpperCase()}</div><span><strong>{booking.client_name}</strong><small>{booking.phone}</small></span></div></td><td><strong className={styles.service}>{booking.service}</strong></td><td><div className={styles.schedule}><strong>{booking.booking_time || booking.time || '—'}</strong><small>{niceDate(booking.booking_date || booking.date)}</small></div></td><td><span className={styles.source}>{booking.source || 'website'}</span></td><td><Status value={booking.status}/></td><td><Actions booking={booking} {...actions}/></td></tr>; }
function BookingCard({booking,niceDate,...actions}) { return <article className={styles.bookingCard}><div className={styles.cardTop}><div className={styles.client}><div>{(booking.client_name||'?').charAt(0).toUpperCase()}</div><span><strong>{booking.client_name}</strong><small>{booking.phone}</small></span></div><Status value={booking.status}/></div><div className={styles.cardService}><Scissors size={15}/><strong>{booking.service}</strong></div><div className={styles.cardMeta}><span><Calendar size={14}/>{niceDate(booking.booking_date||booking.date)}</span><span><Clock size={14}/>{booking.booking_time||booking.time||'—'}</span></div><div className={styles.cardBottom}><span className={styles.source}>{booking.source||'website'}</span><Actions booking={booking} {...actions}/></div></article>; }
